/**
 * frontend/CreatorOnboarding.jsx
 * SoundDAO Sprint 2 — Creator profile setup wizard
 *
 * 5-step wizard:
 *   Step 1 — Artist name + genre
 *   Step 2 — Bio (max 280 chars)
 *   Step 3 — Social links (Spotify, Instagram, SoundCloud, Bandcamp)
 *   Step 4 — Video upload (MP4/MOV, max 50 MB → Supabase Storage 'creator-videos')
 *   Step 5 — Avatar photo (upload to Supabase Storage 'creator-avatars')
 *
 * On completion → PUT /api/creators/:user_id, then navigate to dashboard.
 *
 * Props:
 *   session   — Supabase auth session (required)
 *   user      — users row (required — must have persona === 'creator')
 *   onComplete — callback(updatedUser?) — called after successful save
 *   onSkip     — optional callback to skip out of wizard without saving
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from './lib/supabaseClient';
import { authFetch } from './lib/authFetch';
import './CreatorOnboarding.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const GENRES = [
  'Hip-Hop', 'R&B', 'Pop', 'Electronic', 'House', 'Techno',
  'Trap', 'Lo-Fi', 'Jazz', 'Soul', 'Rock', 'Indie',
  'Classical', 'Reggae', 'Afrobeats', 'Latin', 'Other',
];

const TOTAL_STEPS = 5;
const VIDEO_MAX_BYTES = 50 * 1024 * 1024; // 50 MB

// ── Step indicator ─────────────────────────────────────────────────────────────
function StepDots({ current }) {
  return (
    <div className="co-step-dots">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={i}
          className={`co-step-dot ${i + 1 === current ? 'co-step-dot--active' : ''} ${i + 1 < current ? 'co-step-dot--done' : ''}`}
        />
      ))}
    </div>
  );
}

// ── CreatorOnboarding ──────────────────────────────────────────────────────────
export default function CreatorOnboarding({ session, user, onComplete, onSkip, prefill }) {
  const user_id    = session?.user?.id || user?.id;

  // ── Form state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // Step 1
  const [artistName, setArtistName] = useState(prefill?.artist_name || '');
  const [genre,      setGenre]      = useState(prefill?.genre      || '');

  // Step 2
  const [bio, setBio] = useState(prefill?.bio || '');

  // Step 3
  const [socialLinks, setSocialLinks] = useState({
    spotify:     prefill?.social_links?.spotify     || '',
    instagram:   prefill?.social_links?.instagram   || '',
    soundcloud:  prefill?.social_links?.soundcloud  || '',
    bandcamp:    prefill?.social_links?.bandcamp    || '',
    apple_music: prefill?.social_links?.apple_music || '',
  });

  // Step 4 — Video
  const [videoFile,    setVideoFile]    = useState(null);
  const [videoPreview, setVideoPreview] = useState(prefill?.video_url || null);
  const [videoUrl,     setVideoUrl]     = useState(prefill?.video_url || null);
  const [videoError,   setVideoError]   = useState('');
  const videoInputRef = useRef(null);

  // Step 5 — Avatar
  const [avatarFile,    setAvatarFile]    = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(prefill?.avatar_url || null);
  const [avatarUrl,     setAvatarUrl]     = useState(prefill?.avatar_url || null);
  const fileInputRef = useRef(null);

  // UI state
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState('');
  const [step1Error, setStep1Error] = useState('');

  // ── Pre-fill from creators table on mount ───────────────────────────────────
  // App.js passes prefill={user} (users row) which doesn't have artist_name/genre/
  // bio/social_links. Fetch the creators row once on mount and populate state.
  useEffect(() => {
    if (!user_id) return;
    authFetch(`${API_BASE}/api/creators/${user_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.creator) return;
        const c = data.creator;
        if (c.artist_name) setArtistName(prev => prev || c.artist_name);
        if (c.genre)       setGenre(prev      => prev || c.genre);
        if (c.bio)         setBio(prev        => prev || c.bio);
        if (c.social_links) {
          setSocialLinks(prev => ({
            spotify:     prev.spotify     || c.social_links.spotify     || '',
            instagram:   prev.instagram   || c.social_links.instagram   || '',
            soundcloud:  prev.soundcloud  || c.social_links.soundcloud  || '',
            bandcamp:    prev.bandcamp    || c.social_links.bandcamp    || '',
            apple_music: prev.apple_music || c.social_links.apple_music || '',
          }));
        }
        if (c.video_url && !videoUrl) {
          setVideoUrl(c.video_url);
          setVideoPreview(c.video_url);
        }
        if (c.avatar_url && !avatarUrl) {
          setAvatarUrl(c.avatar_url);
          setAvatarPreview(c.avatar_url);
        }
      })
      .catch(() => {}); // non-fatal — fields just stay empty
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  // ── Navigation ──────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (step === 1) {
      if (!artistName.trim()) {
        setStep1Error('Artist name is required.');
        return;
      }
      setStep1Error('');
    }
    setStep(s => Math.min(s + 1, TOTAL_STEPS));
  }, [step, artistName]);

  const goBack = useCallback(() => {
    setStep(s => Math.max(s - 1, 1));
  }, []);

  // ── Signed-URL upload helper (same pattern as CreatorDashboard) ──────────────
  // Calls the backend to get a short-lived signed URL, then PUTs directly to
  // Supabase Storage. Avoids anon-key RLS issues on creator-videos / creator-avatars.
  const uploadFileViaSignedUrl = useCallback(async (file, bucket) => {
    const ext  = file.name.split('.').pop().toLowerCase();
    const path = `${user_id}/${Date.now()}.${ext}`;

    const signRes = await authFetch(
      `${API_BASE}/api/media/sign-upload?bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`
    );
    if (!signRes.ok) {
      const e = await signRes.json().catch(() => ({}));
      throw new Error(e.error || 'Could not get upload URL');
    }
    const { signedUrl } = await signRes.json();

    const putRes = await fetch(signedUrl, {
      method:  'PUT',
      headers: { 'Content-Type': file.type },
      body:    file,
    });
    if (!putRes.ok) throw new Error(`Storage upload failed (${putRes.status})`);

    const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(path);
    return publicUrl;
  }, [user_id]);

  // ── Video handling ────────────────────────────────────────────────────────
  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoError('');
    if (file.size > VIDEO_MAX_BYTES) {
      setVideoError('Video must be 50 MB or less.');
      e.target.value = '';
      return;
    }
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const uploadVideo = useCallback(async () => {
    if (!videoFile) return videoUrl; // already set from prefill, or no upload
    const url = await uploadFileViaSignedUrl(videoFile, 'creator-videos');
    setVideoUrl(url);
    return url;
  }, [videoFile, videoUrl, uploadFileViaSignedUrl]);

  // ── Avatar handling ──────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const uploadAvatar = useCallback(async () => {
    if (!avatarFile) return avatarUrl; // already set from prefill, or no upload
    const url = await uploadFileViaSignedUrl(avatarFile, 'creator-avatars');
    setAvatarUrl(url);
    return url;
  }, [avatarFile, avatarUrl, uploadFileViaSignedUrl]);

  // ── Save (final step) ────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!user_id) return;
    setSaving(true);
    setSaveError('');
    try {
      const [uploadedVideoUrl, uploadedAvatarUrl] = await Promise.all([
        uploadVideo(),
        uploadAvatar(),
      ]);

      // Sanitise social links — strip empty strings
      const cleanSocials = Object.fromEntries(
        Object.entries(socialLinks).filter(([, v]) => v.trim())
      );

      const body = {
        artist_name:        artistName.trim(),
        genre:              genre  || undefined,
        bio:                bio    || undefined,
        social_links:       Object.keys(cleanSocials).length ? cleanSocials : undefined,
        avatar_url:         uploadedAvatarUrl || undefined,
        video_url:          uploadedVideoUrl  || undefined,
      };

      // Prompt 1.5: ownership is proven by the access token.
      const res = await authFetch(`${API_BASE}/api/creators/${user_id}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Save failed');
      }

      const { creator } = await res.json();
      onComplete?.(creator);
    } catch (err) {
      console.error('[CreatorOnboarding] save error:', err);
      setSaveError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [user_id, uploadVideo, uploadAvatar, socialLinks, artistName, genre, bio, onComplete]);

  // ── Render steps ─────────────────────────────────────────────────────────────
  return (
    <div className="co-screen">
      <div className="co-card">
        {/* Header */}
        <div className="co-header">
          <div className="co-logo">🎤</div>
          <h1 className="co-title">Set Up Your Profile</h1>
          <p className="co-subtitle">Step {step} of {TOTAL_STEPS}</p>
          <StepDots current={step} />
        </div>

        {/* ── Step 1: Name + Genre ── */}
        {step === 1 && (
          <div className="co-body">
            <div className="co-field">
              <label className="co-label">Artist Name</label>
              <input
                className="co-input"
                type="text"
                placeholder="Your artist or band name"
                value={artistName}
                onChange={e => { setArtistName(e.target.value); setStep1Error(''); }}
                maxLength={80}
                autoFocus
              />
              {step1Error && <p className="co-field-error">{step1Error}</p>}
            </div>

            <div className="co-field">
              <label className="co-label">Genre</label>
              <div className="co-genre-grid">
                {GENRES.map(g => (
                  <button
                    key={g}
                    type="button"
                    className={`co-genre-chip ${genre === g ? 'co-genre-chip--active' : ''}`}
                    onClick={() => setGenre(prev => prev === g ? '' : g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Bio ── */}
        {step === 2 && (
          <div className="co-body">
            <div className="co-field">
              <label className="co-label">Bio</label>
              <p className="co-field-hint">Tell fans who you are. Up to 280 characters.</p>
              <textarea
                className="co-textarea"
                placeholder="I make beats that hit different. Based in LA, signed to no one."
                value={bio}
                onChange={e => setBio(e.target.value.slice(0, 280))}
                rows={5}
                autoFocus
              />
              <p className={`co-char-count ${bio.length >= 260 ? 'co-char-count--warn' : ''}`}>
                {bio.length} / 280
              </p>
            </div>
          </div>
        )}

        {/* ── Step 3: Social Links ── */}
        {step === 3 && (
          <div className="co-body">
            <p className="co-field-hint co-field-hint--top">Add links so fans can find your music.</p>
            {[
              { key: 'spotify',     icon: '🟢', label: 'Spotify',     placeholder: 'https://open.spotify.com/artist/...' },
              { key: 'apple_music', icon: '🎵', label: 'Apple Music', placeholder: 'https://music.apple.com/us/artist/...' },
              { key: 'instagram',   icon: '📸', label: 'Instagram',   placeholder: 'https://instagram.com/yourhandle' },
              { key: 'soundcloud',  icon: '🔶', label: 'SoundCloud',  placeholder: 'https://soundcloud.com/yourname' },
              { key: 'bandcamp',    icon: '🅱', label: 'Bandcamp',    placeholder: 'https://yourname.bandcamp.com' },
            ].map(({ key, icon, label, placeholder }) => (
              <div className="co-field co-field--inline" key={key}>
                <span className="co-social-icon">{icon}</span>
                <div className="co-field-grow">
                  <label className="co-label">{label}</label>
                  <input
                    className="co-input"
                    type="url"
                    placeholder={placeholder}
                    value={socialLinks[key]}
                    onChange={e => setSocialLinks(prev => ({ ...prev, [key]: e.target.value }))}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Step 4: Video Upload ── */}
        {step === 4 && (
          <div className="co-body co-body--center">
            <p className="co-field-hint co-field-hint--top">
              Share a short clip — performance, studio session, or anything that represents your sound.
            </p>

            {videoPreview ? (
              <div className="co-video-preview-wrap">
                <video
                  src={videoPreview}
                  className="co-video-preview"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <button
                  type="button"
                  className="co-avatar-remove"
                  onClick={() => {
                    setVideoFile(null);
                    setVideoPreview(null);
                    setVideoUrl(null);
                    setVideoError('');
                  }}
                >
                  Remove video
                </button>
              </div>
            ) : (
              <div
                className="co-video-zone"
                onClick={() => videoInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && videoInputRef.current?.click()}
              >
                <div className="co-avatar-placeholder-icon">🎬</div>
                <p className="co-avatar-placeholder-text">Click to upload video</p>
                <p className="co-avatar-placeholder-sub">MP4 or MOV — max 50 MB</p>
              </div>
            )}

            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/mov"
              style={{ display: 'none' }}
              onChange={handleVideoChange}
            />

            {videoError && <p className="co-field-error" style={{ textAlign: 'center' }}>{videoError}</p>}

            <p className="co-field-hint" style={{ textAlign: 'center', marginTop: 8 }}>
              Optional — you can add or change this later.
            </p>
          </div>
        )}

        {/* ── Step 5: Avatar ── */}
        {step === 5 && (
          <div className="co-body co-body--center">
            <p className="co-field-hint co-field-hint--top">Add a photo so fans can recognise you.</p>

            <div
              className="co-avatar-zone"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" className="co-avatar-preview" />
              ) : (
                <>
                  <div className="co-avatar-placeholder-icon">🖼️</div>
                  <p className="co-avatar-placeholder-text">Click to upload photo</p>
                  <p className="co-avatar-placeholder-sub">JPG, PNG, WebP — max 5 MB</p>
                </>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {avatarPreview && (
              <button
                type="button"
                className="co-avatar-remove"
                onClick={() => { setAvatarFile(null); setAvatarPreview(null); setAvatarUrl(null); }}
              >
                Remove photo
              </button>
            )}

            {saveError && <p className="co-save-error">{saveError}</p>}
          </div>
        )}

        {/* Footer nav */}
        <div className="co-footer">
          {step > 1 ? (
            <button className="co-btn co-btn--ghost" onClick={goBack} disabled={saving}>
              ← Back
            </button>
          ) : (
            onSkip ? (
              <button className="co-btn co-btn--ghost" onClick={onSkip}>
                Skip for now
              </button>
            ) : <div />
          )}

          {step < TOTAL_STEPS ? (
            <button className="co-btn co-btn--primary" onClick={goNext}>
              Next →
            </button>
          ) : (
            <button
              className={`co-btn co-btn--primary ${saving ? 'co-btn--loading' : ''}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
